import { PlusIcon } from "@images/svgIcons/plusIcon"
import { useCurrentBox } from "@modules/box/useBox"
import { BoxOfferCard } from "@modules/boxOffer/components/boxOfferCard"
import { BoxOfferLiteVersion, BoxOfferType } from "@modules/boxOffer/entities/boxOfferTypes"
import { StatusFilter } from "@modules/boxOffer/gateway/boxOfferDto"
import { useBoxOffers } from "@modules/boxOffer/hooks/useBoxOffer"
import { useBoxOfferSidebar } from "@modules/boxOffer/hooks/useBoxOfferSidebar"
import { useBoxProgrammations } from "@modules/boxProgrammation/hooks/useBoxProgrammations"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@modules/core/utils/i18n"
import { Path } from "@modules/navigation/routes"
import { useBoxNavigate } from "@modules/navigation/useBoxNavigate"
import { StripeAccountStatuEnum } from "@modules/payment/stripeTypes"
import { ScreenHeader } from "@modules/ui/components/header"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { useToast } from "@modules/ui/components/huToast"
import { HUStack } from "@modules/ui/components/layout"
import TooltipIndicator from "@modules/ui/components/tooltipIndicator"
import { isValueInEnum } from "@modules/utils/utils"
import { InputText } from "primereact/inputtext"
import { Tooltip } from "primereact/tooltip"
import { useCallback, useMemo, useState } from "react"
import { useTheme } from "styled-components"

export const BoxOfferPage: React.FC = () => {
  const { data: offers } = useBoxOffers()
  const { data: archivedOffers } = useBoxOffers({ statusFilter: StatusFilter.ARCHIVED })
  const { navigateToBoxOfferCards, navigateToBoxOfferResume, isSidebarVisible } = useBoxOfferSidebar()
  const toast = useToast()
  const boxNavigate = useBoxNavigate()
  const { result: box } = useCurrentBox()
  const { result: programs } = useBoxProgrammations({ size: 50 })
  const [filter, setFilter] = useState<BoxOfferType | "ALL" | "ARCHIVED">("ALL")
  const [researchFilter, setResearchFilter] = useState("")
  const theme = useTheme()
  const canCreateOffer = useBoxStaffPermission(BoxStaffPermissions.CREATE_OFFER)

  const isBoxOfferLiteVersion = useMemo(
    () =>
      box &&
      (box.stripeAccountStatus === StripeAccountStatuEnum.REQUIRE_ADDITIONAL_INFORMATION ||
        box.stripeAccountStatus === StripeAccountStatuEnum.UNDEFINED)
        ? true
        : false,
    [box],
  )

  const filteredOffers = useMemo(() => {
    if (filter === "ARCHIVED") {
      return archivedOffers.filter(
        (offer) =>
          offer.name.toLowerCase().indexOf(researchFilter.toLowerCase()) >= 0 ||
          (offer.description && offer.description.toLowerCase().indexOf(researchFilter.toLowerCase()) >= 0),
      )
    }

    return offers
      .filter((offer) => (filter === "ALL" ? offer : offer.type === filter))
      .filter(
        (offer) =>
          offer.name.toLowerCase().indexOf(researchFilter.toLowerCase()) >= 0 ||
          (offer.description && offer.description.toLowerCase().indexOf(researchFilter.toLowerCase()) >= 0),
      )
  }, [filter, researchFilter, offers])

  const onAdd = useCallback(() => {
    if (programs && programs.length > 0)
      return isSidebarVisible
        ? undefined
        : navigateToBoxOfferCards(filter !== "ALL" && filter !== "ARCHIVED" ? filter : undefined)
    else return toast.show({ severity: "info", detail: word("box.offer.form.error.createProgramFirst") })
  }, [isSidebarVisible, filter, programs])

  const liteVersionInformation = () => {
    return (
      <HUText className="flex flex-row align-items-center ml-2" color={theme.error}>
        - {word("offer_list.lite_version.title")}
        <TooltipIndicator
          className="tooltip ml-2 black"
          data-pr-showdelay={200}
          data-pr-hidedelay={200}
          data-pr-position="bottom"
        />
        <Tooltip className="tooltip-container" target=".tooltip" autoHide={false}>
          <div className="tooltip-content text-justify">
            <HUText type="label" fontStyle="BM">
              {word("offer_list.lite_version.tooltip")}
              <span
                onClick={() => boxNavigate(Path.Box.Admin + "?tab=banking")}
                className="cursor-pointer underline"
                style={{ color: theme.mainWhite }}
              >
                {word("global.here")}
              </span>
            </HUText>
          </div>
        </Tooltip>
      </HUText>
    )
  }

  return (
    <HUStack gap={0} flex style={{ width: "100%" }}>
      <ScreenHeader
        title={word("menu.offers")}
        subTitle={word("offer.header.subtitle")}
        rightButton={isBoxOfferLiteVersion && liteVersionInformation()}
      />
      <div className="flex mb-5 gap-2 flex-wrap">
        <HUButton
          type="Default"
          size="M"
          colorType={filter === "ALL" ? "White" : "Quaternary"}
          text={word("global.filters.all")}
          onClick={() => setFilter("ALL")}
        />
        {Object.values(BoxOfferType).map((type, index) => (
          <HUButton
            key={`${index}-${type}`}
            type="Default"
            size="M"
            colorType={filter === type ? "White" : "Quaternary"}
            text={word(`box.offer.form.types.${type}`)}
            onClick={() => setFilter(type)}
            disabled={isBoxOfferLiteVersion && !isValueInEnum(type, BoxOfferLiteVersion) ? true : false}
          />
        ))}
        <HUButton
          type="Default"
          size="M"
          colorType={filter === "ARCHIVED" ? "White" : "Quaternary"}
          text={word("global.archived", { count: 2 })}
          onClick={() => setFilter("ARCHIVED")}
        />
        <div className="p-input-icon-left md:ml-auto align-content-center">
          <i className="pi pi-search" />
          <InputText
            type="search"
            onChange={(e) => setResearchFilter(e.target.value)}
            placeholder={word("global.search.placeholder")}
            className="w-15rem"
            value={researchFilter}
          />
        </div>
      </div>
      <section className="flex flex-wrap gap-3">
        {/* PERMISSIONS - Head-coach & Coach & Financial permissions denied */}
        {canCreateOffer && filter !== "ARCHIVED" && (
          <div className="flex flex-column box-offer-card-width h-9rem p-3 gap-4 border-round-xl border-1 justify-content-between border-dashed border-primary-700">
            <HUText fontStyle="TM">
              {filter === "ALL" ? word("box.offer.add.title") : word(`offer.form.${filter}.create`)}
            </HUText>
            <HUButton
              type="Rounded"
              size="M"
              colorType="Primary"
              text={word("global.new")}
              icon={{ icon: PlusIcon }}
              onClick={onAdd}
            />
          </div>
        )}
        {filteredOffers.length > 0 &&
          filteredOffers.map((offer) => (
            <BoxOfferCard
              offer={offer}
              key={offer.id}
              onClick={() =>
                !isSidebarVisible && navigateToBoxOfferResume ? navigateToBoxOfferResume(offer.id) : undefined
              }
              className={`flex box-offer-card-width h-9rem p-3 gap-4
                border-round-xl
                justify-content-between
                 cursor-pointer
                ${offer.archived ? "border-none bg-neutralColor-50" : "border-1 border-neutralColor-300"}`}
            />
          ))}
      </section>
    </HUStack>
  )
}
